<template>
  <nav class="governance-sidebar" :class="{ collapsed: isCollapsed }" :aria-label="`${sidebarTitle || '平台'}导航`">
    <!-- 顶部：标题 + 折叠按钮 -->
    <div class="sidebar-header">
      <div v-if="!isCollapsed" class="sidebar-brand">
        <span class="sidebar-domain-icon" aria-hidden="true">
          <component v-if="currentDomainIcon" :is="currentDomainIcon" />
        </span>
        <span class="sidebar-title-block">
          <span class="sidebar-title" :title="sidebarTitle">{{ sidebarTitle }}</span>
          <span class="sidebar-subtitle" :title="domainSubtitle">{{ domainSubtitle }}</span>
        </span>
      </div>
      <!-- 三横线折叠按钮（与会话列表一致） -->
      <button
        class="collapse-menu-btn"
        type="button"
        :aria-label="isCollapsed ? '展开平台菜单' : '折叠平台菜单'"
        @click="toggleCollapse"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="15" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
    </div>

    <section v-if="!isCollapsed" class="domain-console-panel" :aria-label="`${sidebarTitle}概览`">
      <div class="domain-console-row">
        <span class="domain-console-label">{{ domainConsoleLabel }}</span>
        <span class="domain-console-status">{{ domainConsoleStatus }}</span>
      </div>
      <div class="domain-console-meta">
        <span>{{ leafMenuCount }} 项能力</span>
        <span>{{ menuGroups.length }} 组菜单</span>
      </div>
    </section>

    <!-- 搜索框：单层胶囊样式，图标与输入文本分栏，避免内外两层割裂 -->
    <div v-if="!isCollapsed" class="search-wrapper">
      <label class="search-label" for="governance-sidebar-search">搜索</label>
      <div class="search-box">
        <!-- 固定图标槽 + flex gap，避免绝对定位下列宽不足导致字与标重叠 -->
        <span class="search-icon-slot" aria-hidden="true">
          <icon-search class="search-icon" :size="14" />
        </span>
        <input
          id="governance-sidebar-search"
          v-model="searchKeyword"
          type="text"
          class="search-input"
          placeholder="菜单、功能或页面"
          :aria-label="`${sidebarTitle || '平台'}菜单搜索`"
          autocomplete="off"
          enterkeyhint="search"
        />
        <button
          v-if="searchKeyword"
          class="search-clear"
          type="button"
          aria-label="清空菜单搜索"
          @click="searchKeyword = ''"
        >
          <icon-close :size="10" />
        </button>
      </div>
    </div>

    <!-- 菜单列表 -->
    <div class="menu-container">
      <!-- 搜索结果模式 -->
      <template v-if="searchKeyword.trim()">
        <div v-if="searchResults.length > 0" class="search-results">
          <RouterLink
            v-for="result in searchResults"
            :key="result.item.key"
            :to="result.item.route || route.path"
            class="search-result-item"
            @click="handleMenuLinkClick(result.item)"
          >
            <span class="result-title">{{ result.item.title }}</span>
            <span class="result-breadcrumb">{{ result.breadcrumb.join(' / ') }}</span>
          </RouterLink>
        </div>
        <div v-else class="empty-result">
          <icon-search class="empty-icon" />
          <span>未找到相关菜单</span>
        </div>
      </template>

      <!-- 正常菜单模式 -->
      <template v-else>
        <section
          v-if="!isCollapsed && priorityMenuItems.length > 0"
          class="priority-section"
          :aria-label="`${sidebarTitle}常用入口`"
        >
          <div class="priority-section-title">常用入口</div>
          <div class="priority-links">
            <RouterLink
              v-for="item in priorityMenuItems"
              :key="item.key"
              :to="item.route || route.path"
              class="priority-link"
              :class="{ active: activeKey === item.key }"
              :aria-current="activeKey === item.key ? 'page' : undefined"
              @click="handleMenuLinkClick(item)"
            >
              <span class="priority-link-mark" aria-hidden="true">{{ getItemInitial(item.title) }}</span>
              <span class="priority-link-copy">
                <span class="priority-link-title" :title="item.title">{{ item.title }}</span>
                <span class="priority-link-hint" :title="getPriorityHint(item)">{{ getPriorityHint(item) }}</span>
              </span>
            </RouterLink>
          </div>
        </section>

        <div
          v-for="group in menuGroups"
          :key="group.key"
          class="menu-group"
        >
          <!-- 分组标题 -->
          <button
            type="button"
            class="group-header"
            :class="{ expanded: expandedGroups.includes(group.key) }"
            :aria-expanded="expandedGroups.includes(group.key)"
            :aria-label="`${expandedGroups.includes(group.key) ? '收起' : '展开'}${group.title}`"
            :title="group.title"
            @click="toggleGroup(group.key)"
          >
            <span class="group-title-wrap">
              <span class="group-icon" aria-hidden="true">
                <component v-if="group.icon" :is="group.icon" />
                <span v-else>{{ getGroupInitial(group.title) }}</span>
              </span>
              <span class="group-title" :title="group.title">{{ group.title }}</span>
            </span>
            <span class="group-count">{{ countLeafItems(group) }}</span>
            <icon-down class="expand-icon" :class="{ rotated: expandedGroups.includes(group.key) }" />
          </button>

          <!-- 子菜单列表 -->
          <Transition name="menu-expand">
            <div v-if="expandedGroups.includes(group.key)" class="menu-items">
              <template v-for="item in group.children" :key="item.key">
                <!-- 有三级子菜单的二级菜单项 -->
                <template v-if="item.children && item.children.length > 0">
                  <button
                    type="button"
                    class="menu-item has-children"
                    :class="{ expanded: expandedSubmenus.includes(item.key) }"
                    :aria-expanded="expandedSubmenus.includes(item.key)"
                    @click="toggleSubmenu(item.key)"
                  >
                    <span class="menu-item-main">
                      <span class="item-dot" aria-hidden="true"></span>
                      <span class="item-title" :title="item.title">{{ item.title }}</span>
                    </span>
                    <span class="item-count">{{ countLeafItems(item) }}</span>
                    <icon-down class="submenu-expand-icon" :class="{ rotated: expandedSubmenus.includes(item.key) }" />
                  </button>
                  <!-- 三级子菜单 -->
                  <Transition name="menu-expand">
                    <div v-if="expandedSubmenus.includes(item.key)" class="submenu-items">
                      <RouterLink
                        v-for="subItem in item.children"
                        :key="subItem.key"
                        :to="subItem.route || route.path"
                        class="menu-item submenu-item"
                        :class="{ active: activeKey === subItem.key }"
                        :aria-current="activeKey === subItem.key ? 'page' : undefined"
                        @click="handleMenuLinkClick(subItem)"
                      >
                        <span class="menu-item-main">
                          <span class="item-dot" aria-hidden="true"></span>
                          <span class="item-title" :title="subItem.title">{{ subItem.title }}</span>
                        </span>
                      </RouterLink>
                    </div>
                  </Transition>
                </template>
                <!-- 普通二级菜单项（无子菜单） -->
                <RouterLink
                  v-else
                  :to="item.route || route.path"
                  class="menu-item"
                  :class="{ active: activeKey === item.key }"
                  :aria-current="activeKey === item.key ? 'page' : undefined"
                  @click="handleMenuLinkClick(item)"
                >
                  <span class="menu-item-main">
                    <span class="item-dot" aria-hidden="true"></span>
                    <span class="item-title" :title="item.title">{{ item.title }}</span>
                  </span>
                </RouterLink>
              </template>
            </div>
          </Transition>
        </div>
      </template>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { PRODUCT_MENU_MAP, findDomainByRoute, type MenuItem } from '@/config/menuConfig'
import { matchProductPrefix } from '@/constants/routeConfig'
import { filterMenuTreeByAccess } from '@/router/permissions'
import { useAuthStore } from '@/stores/auth'
import IconClose from '@arco-design/web-vue/es/icon/icon-close'
import IconDown from '@arco-design/web-vue/es/icon/icon-down'
import IconSearch from '@arco-design/web-vue/es/icon/icon-search'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const authStore = useAuthStore()

// 定义 emits
const emit = defineEmits<{
  (e: 'collapse', collapsed: boolean): void
}>()

// 侧边栏收起状态
const isCollapsed = ref(false)

// 切换收起状态
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  emit('collapse', isCollapsed.value)
}

// 搜索关键词
const searchKeyword = ref('')

// 当前激活的菜单项
const activeKey = ref('')

const currentProductConfig = computed(() => {
  // 优先按 meta.menuGroup + 路由精确/前缀匹配到九域中的某个域，只显示该域菜单
  const menuGroup = (route.meta?.menuGroup as string | undefined) ?? undefined
  const domain = findDomainByRoute(route.path, menuGroup)
  if (domain) {
    return { key: domain.key, title: domain.title, menu: domain.menu }
  }
  // 降级：路由既无 menuGroup 也不在任何域菜单叶子节点中，按前缀匹配全量菜单
  const prefix = matchProductPrefix(route.path)
  const fallback = prefix ? PRODUCT_MENU_MAP[prefix] : null
  return fallback ? { key: prefix, title: fallback.title, menu: fallback.menu } : null
})

const menuGroups = computed(() => {
  return filterMenuTreeByAccess(currentProductConfig.value?.menu || [], authStore.canAccessRoute)
})

const sidebarTitle = computed(() => {
  return currentProductConfig.value?.title || ''
})

const DOMAIN_SUBTITLES: Record<string, string> = {
  'agent-workspace': '对话 · 执行 · 工具',
  workbench: '业务 · 决策 · 增长',
  data: '目录 · 开发 · 治理',
  agent: '构建 · 路由 · 评测',
  operations: '监控 · 调度 · 协同',
  admin: '权限 · 成本 · 系统',
}

const DOMAIN_CONSOLE_LABELS: Record<string, string> = {
  'agent-workspace': '智能体工作台',
  workbench: '业务工作台',
  data: '数据控制台',
  agent: '工程控制台',
  operations: '运行控制台',
  admin: '管理控制台',
}

const DOMAIN_CONSOLE_STATUS: Record<string, string> = {
  'agent-workspace': '工作流入口',
  workbench: '业务执行',
  data: '数据治理',
  agent: '生产编排',
  operations: '自动化运行',
  admin: '平台管控',
}

const currentDomainKey = computed(() => currentProductConfig.value?.key || 'default')
const domainSubtitle = computed(() => DOMAIN_SUBTITLES[currentDomainKey.value] || 'Console · Control · Govern')
const domainConsoleLabel = computed(() => DOMAIN_CONSOLE_LABELS[currentDomainKey.value] || 'Product Console')
const domainConsoleStatus = computed(() => DOMAIN_CONSOLE_STATUS[currentDomainKey.value] || '产品导航')
const currentDomainIcon = computed(() => menuGroups.value.find(group => group.icon)?.icon ?? null)

function countLeafItems(item: MenuItem): number {
  if (!item.children?.length) return item.route ? 1 : 0
  return item.children.reduce((total, child) => total + countLeafItems(child), 0)
}

function collectRouteItems(items: MenuItem[]): MenuItem[] {
  const result: MenuItem[] = []
  for (const item of items) {
    if (item.route) result.push(item)
    if (item.children?.length) result.push(...collectRouteItems(item.children))
  }
  return result
}

const leafMenuCount = computed(() =>
  menuGroups.value.reduce((total, group) => total + countLeafItems(group), 0)
)

const priorityMenuItems = computed(() => collectRouteItems(menuGroups.value).slice(0, 4))

const PRIORITY_HINTS: Record<string, string> = {
  'agent-list': '目录与发布',
  'capability-config': '工具链配置',
  'prompt-engineering': '提示词治理',
  'agent-model-settings': '模型路由',
  'governance-knowledge-base': '知识资产',
  'governance-metrics': '指标语义',
  'governance-projects': '项目权限',
  'governance-model-cost': '成本控制',
}

function getPriorityHint(item: MenuItem): string {
  return PRIORITY_HINTS[item.key] || '快速进入'
}

function getItemInitial(title: string): string {
  const chars = Array.from(title).filter(char => /[A-Za-z0-9\u4e00-\u9fa5]/.test(char))
  return chars.slice(0, 2).join('').toUpperCase() || 'DM'
}

function getGroupInitial(title: string): string {
  const chars = Array.from(title).filter(char => /[A-Za-z0-9\u4e00-\u9fa5]/.test(char))
  return chars.slice(0, 1).join('').toUpperCase() || 'D'
}

// 展开的分组 - 默认全部关闭
const expandedGroups = ref<string[]>([])

// 展开的子菜单（三级菜单）
const expandedSubmenus = ref<string[]>([])

// 切换分组展开状态
function toggleGroup(key: string) {
  const index = expandedGroups.value.indexOf(key)
  if (index > -1) {
    expandedGroups.value.splice(index, 1)
  } else {
    expandedGroups.value.push(key)
  }
}

// 切换子菜单展开状态
function toggleSubmenu(key: string) {
  const index = expandedSubmenus.value.indexOf(key)
  if (index > -1) {
    expandedSubmenus.value.splice(index, 1)
  } else {
    expandedSubmenus.value.push(key)
  }
}

// 搜索结果类型
interface SearchResult {
  item: MenuItem
  breadcrumb: string[]
}

// 递归搜索菜单项
function searchMenuItems(
  items: MenuItem[],
  keyword: string,
  breadcrumb: string[] = []
): SearchResult[] {
  const results: SearchResult[] = []
  const lowerKeyword = keyword.toLowerCase()

  for (const item of items) {
    const currentBreadcrumb = [...breadcrumb, item.title]

    if (item.route && item.title.toLowerCase().includes(lowerKeyword)) {
      results.push({
        item,
        breadcrumb: currentBreadcrumb
      })
    }

    if (item.children && item.children.length > 0) {
      const childResults = searchMenuItems(item.children, keyword, currentBreadcrumb)
      results.push(...childResults)
    }
  }

  return results
}

// 搜索结果
const searchResults = computed<SearchResult[]>(() => {
  const keyword = searchKeyword.value.trim()
  if (!keyword) return []
  return searchMenuItems(menuGroups.value, keyword)
})

// 点击菜单链接
function handleMenuLinkClick(item: MenuItem) {
  activeKey.value = item.key
  searchKeyword.value = ''
}

// 递归查找菜单项，返回菜单项及其父级路径
interface MenuSearchResult {
  item: MenuItem
  parentKeys: string[]
}

interface ActiveMenuMatch extends MenuSearchResult {
  groupKey: string
}

function findMenuByRoute(
  items: MenuItem[],
  path: string,
  parentKeys: string[] = []
): MenuSearchResult | null {
  for (const item of items) {
    if (item.route === path) {
      return { item, parentKeys }
    }
    if (item.children) {
      const found = findMenuByRoute(item.children, path, [...parentKeys, item.key])
      if (found) return found
    }
  }
  return null
}

function findActiveMenuMatch(groups: MenuItem[], path: string): ActiveMenuMatch | null {
  for (const group of groups) {
    const result = findMenuByRoute(group.children || [], path, [group.key])
    if (result) return { ...result, groupKey: group.key }
  }
  return null
}

function focusRouteMenu(path: string): boolean {
  const result = findActiveMenuMatch(menuGroups.value, path)
  if (!result) return false
  activeKey.value = result.item.key
  expandedGroups.value = [result.groupKey]
  expandedSubmenus.value = result.parentKeys.filter(parentKey => parentKey !== result.groupKey)
  return true
}

function focusDefaultMenuGroup(groups: MenuItem[]) {
  activeKey.value = ''
  expandedSubmenus.value = []
  expandedGroups.value = groups[0] ? [groups[0].key] : []
}

// 域切换时只展开当前路由所在分组；未知路由展开首组，避免全量菜单压迫主任务。
watch(
  menuGroups,
  (groups) => {
    if (!focusRouteMenu(route.path)) {
      focusDefaultMenuGroup(groups)
    }
  },
  { immediate: true }
)

// 根据路由自动定位当前菜单，所有九宫格产品域共用同一展开规则。
watch(
  () => route.path,
  (path) => {
    if (!focusRouteMenu(path)) {
      focusDefaultMenuGroup(menuGroups.value)
    }
  },
  { immediate: true }
)
</script>

<style scoped lang="scss">
// Claude 素色：侧边栏用 subtle 比主区深一档，视觉对比明显
$color-bg: var(--c-bg-subtle);
$color-bg-hover: var(--c-bg-hover);
$color-bg-active: var(--c-accent-bg);
$color-text-primary: var(--color-text-primary);
$color-text-secondary: var(--color-text-secondary);
$color-text-tertiary: var(--color-text-tertiary);
$color-primary: var(--color-primary);
$color-border: var(--color-border-light);

.governance-sidebar {
  --sidebar-accent: var(--dm-color-accent, var(--c-accent-active));
  --sidebar-info: var(--dm-color-info, var(--c-info));
  --sidebar-success: var(--dm-color-success, var(--c-success));
  --sidebar-panel: color-mix(in srgb, var(--dm-color-surface) 72%, $color-bg);
  --sidebar-border: color-mix(in srgb, var(--dm-color-border) 88%, var(--sidebar-accent) 12%);
  width: 256px;
  height: 100%;
  background: $color-bg;
  border-right: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition:
    width 0.22s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.18s ease;

  &.collapsed {
    width: 64px;

    .sidebar-header {
      padding: 12px 10px;
      justify-content: center;
    }

    .search-wrapper,
    .domain-console-panel,
    .priority-section {
      display: none;
    }

    .menu-container {
      display: block;
      padding-top: 6px;
    }

    .menu-group {
      margin-bottom: 4px;
    }

    .group-header {
      width: 40px;
      height: 40px;
      padding: 0;
      margin: 3px 12px;
      justify-content: center;
      border-radius: 8px;
    }

    .group-title,
    .group-count,
    .expand-icon,
    .menu-items {
      display: none;
    }

    .group-title-wrap {
      gap: 0;
    }
  }
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 12px 10px;
  flex-shrink: 0;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.sidebar-domain-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  border: 1px solid color-mix(in srgb, var(--sidebar-accent) 24%, transparent);
  border-radius: 8px;
  background: color-mix(in srgb, var(--sidebar-accent) 10%, var(--dm-color-surface));
  color: var(--sidebar-accent);
}

.sidebar-title-block {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.sidebar-title {
  font-size: 14px;
  font-weight: 760;
  color: $color-text-primary;
  letter-spacing: 0;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-subtitle {
  margin-top: 2px;
  color: $color-text-tertiary;
  font-size: 10px;
  font-weight: 650;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.collapse-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: $color-text-tertiary;
  cursor: pointer;
  transition: color 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: $color-text-secondary;
    background: $color-bg-hover;
  }

  &:active {
    background: color-mix(in srgb, var(--sidebar-accent) 11%, $color-bg);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--dm-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 24%, transparent));
  }
}

.domain-console-panel {
  margin: 0 12px 10px;
  padding: 10px 12px;
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  background: var(--sidebar-panel);
}

.domain-console-row,
.domain-console-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.domain-console-label {
  color: $color-text-primary;
  font-size: 12px;
  font-weight: 760;
}

.domain-console-status {
  display: inline-flex;
  align-items: center;
  min-height: 20px;
  padding: 0 7px;
  border: 1px solid color-mix(in srgb, var(--sidebar-success) 24%, transparent);
  border-radius: 999px;
  background: color-mix(in srgb, var(--sidebar-success) 9%, var(--sidebar-panel));
  color: var(--sidebar-success);
  font-size: 10px;
  font-weight: 760;
  white-space: nowrap;
}

.domain-console-meta {
  margin-top: 8px;
  color: $color-text-tertiary;
  font-size: 11px;
  font-weight: 650;
}

.search-wrapper {
  padding: 0 12px 10px;
  flex-shrink: 0;
}

.search-label {
  display: block;
  margin: 0 2px 6px;
  color: $color-text-tertiary;
  font-size: 11px;
  font-weight: 720;
  line-height: 1;
}

.search-box {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 38px;
  padding: 0 10px;
  border-radius: 8px;
  background: color-mix(in srgb, var(--dm-color-surface) 70%, $color-bg);
  border: 1px solid var(--sidebar-border);
  transition:
    color 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: color-mix(in srgb, var(--dm-color-surface) 78%, $color-bg);
    border-color: color-mix(in srgb, var(--sidebar-accent) 20%, var(--sidebar-border));
  }

  &:focus-within {
    background: color-mix(in srgb, var(--dm-color-surface) 82%, $color-bg);
    border-color: $color-primary;
    box-shadow: var(--dm-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 20%, transparent));
  }
}

.search-icon-slot {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  color: $color-text-tertiary;
}

.search-icon {
  color: $color-text-tertiary;
  pointer-events: none;
  transition: color 0.2s ease;
}

.search-box:focus-within .search-icon {
  color: $color-primary;
}

.search-input {
  all: unset;
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  display: block;
  height: 100%;
  padding: 0;
  line-height: 1.45;
  font-size: 13px;
  font-family: inherit;
  color: $color-text-primary;
  caret-color: $color-primary;

  &::placeholder {
    color: $color-text-tertiary;
  }

  &:focus {
    outline: none;
  }

  &::-webkit-search-decoration,
  &::-webkit-search-cancel-button,
  &::-webkit-search-results-button,
  &::-webkit-search-results-decoration {
    appearance: none;
    -webkit-appearance: none;
  }
}

.search-clear {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  margin: 0;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: $color-text-tertiary;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: $color-text-tertiary;
  }
}

.menu-container {
  flex: 1;
  overflow-y: auto;
  padding: 4px 0 10px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 2px;
    transition: background 0.2s;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: var(--color-border);
  }
}

.menu-group {
  margin-bottom: 5px;
}

.group-header {
  appearance: none;
  width: calc(100% - 16px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  margin: 2px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;

  &:hover {
    background: $color-bg-hover;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--dm-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 24%, transparent));
  }

  &.expanded {
    border-color: color-mix(in srgb, var(--sidebar-accent) 14%, transparent);
    background: color-mix(in srgb, var(--sidebar-accent) 5%, transparent);

    .expand-icon {
      transform: rotate(180deg);
    }
  }
}

.group-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.group-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
  border-radius: 8px;
  background: color-mix(in srgb, var(--sidebar-accent) 8%, var(--dm-color-surface));
  color: var(--sidebar-accent);
  font-size: 11px;
  font-weight: 760;
}

.group-title {
  font-size: 13px;
  font-weight: 720;
  color: $color-text-primary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.group-count,
.item-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: color-mix(in srgb, $color-text-primary 7%, transparent);
  color: $color-text-tertiary;
  font-size: 10px;
  font-weight: 760;
}

.expand-icon {
  font-size: 12px;
  color: $color-text-tertiary;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-items {
  padding: 2px 0;
  overflow: hidden;
}

.menu-item {
  position: relative;
  width: calc(100% - 16px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  margin: 1px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: left;
  text-decoration: none;
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  .item-title {
    font-size: 13px;
    color: $color-text-secondary;
    padding-left: 0;
    line-height: 1.5;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: $color-bg-hover;

    .item-title {
      color: $color-text-primary;
    }
  }

  &.active {
    border-color: color-mix(in srgb, var(--sidebar-accent) 22%, transparent);
    background: $color-bg-active;

    &::before {
      content: '';
      position: absolute;
      left: -1px;
      top: 9px;
      bottom: 9px;
      width: 3px;
      border-radius: 999px;
      background: var(--sidebar-accent);
    }

    .item-title {
      color: $color-primary;
      font-weight: 760;
    }

    .item-dot {
      background: var(--sidebar-accent);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 18%, transparent);
    }
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--dm-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 24%, transparent));
  }

  &.has-children {
    .submenu-expand-icon {
      font-size: 12px;
      color: $color-text-tertiary;
      transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
      margin-right: 4px;

      &.rotated {
        transform: rotate(180deg);
      }
    }

    &.expanded {
      .item-title {
        color: $color-text-primary;
        font-weight: 500;
      }
    }
  }
}

.menu-item-main {
  display: flex;
  align-items: center;
  gap: 9px;
  flex: 1;
  min-width: 0;
}

.item-dot {
  width: 6px;
  height: 6px;
  flex: 0 0 auto;
  border-radius: 999px;
  background: color-mix(in srgb, $color-text-tertiary 58%, transparent);
}

.submenu-items {
  padding: 1px 0;
  overflow: hidden;
}

.menu-item.submenu-item {
  width: calc(100% - 30px);
  margin-left: 22px;
  margin-right: 8px;

  .item-title {
    font-size: 12px;
  }
}

.priority-section {
  margin: 0 8px 10px;
  padding: 8px;
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  background: transparent;
}

.priority-section-title {
  margin: 0 2px 7px;
  color: $color-text-tertiary;
  font-size: 11px;
  font-weight: 760;
}

.priority-links {
  display: grid;
  gap: 6px;
}

.priority-link {
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
  min-height: 40px;
  padding: 6px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: inherit;
  text-decoration: none;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;

  &:hover,
  &:focus-visible,
  &.active {
    outline: none;
    border-color: color-mix(in srgb, var(--sidebar-accent) 24%, transparent);
    background: color-mix(in srgb, var(--sidebar-accent) 8%, var(--dm-color-surface));
  }

  &:focus-visible {
    box-shadow: var(--dm-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 24%, transparent));
  }
}

.priority-link-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--sidebar-accent) 72%, transparent);
  color: transparent;
  font-size: 0;
}

.priority-link-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.priority-link-title {
  color: $color-text-primary;
  font-size: 12px;
  font-weight: 760;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.priority-link-hint {
  margin-top: 2px;
  color: $color-text-tertiary;
  font-size: 10px;
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-results {
  padding: 4px 0;
}

.search-result-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 14px;
  margin: 2px 8px;
  border-radius: 8px;
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover {
    background: $color-bg-hover;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--dm-focus-ring, 0 0 0 3px color-mix(in srgb, var(--sidebar-accent) 24%, transparent));
  }

  .result-title {
    font-size: 13px;
    font-weight: 500;
    color: $color-text-primary;
  }

  .result-breadcrumb {
    font-size: 11px;
    color: $color-text-tertiary;
  }
}

.empty-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: $color-text-tertiary;

  .empty-icon {
    font-size: 36px;
    margin-bottom: 12px;
    opacity: 0.4;
  }

  span {
    font-size: 13px;
    color: $color-text-tertiary;
  }
}

.menu-expand-enter-active,
.menu-expand-leave-active {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: 500px;
}

.menu-expand-enter-from,
.menu-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

@media (prefers-reduced-motion: reduce) {
  .governance-sidebar,
  .collapse-menu-btn,
  .search-box,
  .search-icon,
  .group-header,
  .expand-icon,
  .menu-item,
  .submenu-expand-icon,
  .priority-link,
  .search-result-item,
  .menu-expand-enter-active,
  .menu-expand-leave-active {
    transition: none;
  }
}
</style>
